import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { BrandMark } from '@/components/generals/brand-mark'
import { mainNav, siteConfig } from '@/constants/site'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-foreground text-background'>
            <div className='mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-6'>
                        <Link href='/' className='inline-flex items-center gap-3'>
                            <BrandMark className='size-10 text-background' />

                            <span className='font-heading text-2xl tracking-tight'>
                                {siteConfig.name}
                            </span>
                        </Link>

                        <p className='mt-6 max-w-md leading-relaxed text-background/75'>
                            Notes on warm, lived-in homes: the rooms, the materials and the small decisions that make a house feel like yours.
                        </p>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-2 lg:col-span-6'>
                        <div>
                            <h2 className='text-xs font-medium uppercase tracking-[0.22em] text-background/65'>
                                Explore
                            </h2>

                            <ul className='mt-5 space-y-3'>
                                <li>
                                    <Link href='/' className='text-background/85 transition-colors hover:text-background'>
                                        Home
                                    </Link>
                                </li>

                                {mainNav.map(item => (
                                    <li key={item.href}>
                                        <Link href={item.href} className='text-background/85 transition-colors hover:text-background'>
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h2 className='text-xs font-medium uppercase tracking-[0.22em] text-background/65'>
                                Work together
                            </h2>

                            <p className='mt-5 leading-relaxed text-background/75'>
                                Planning a single room or a whole home? I would love to hear about it.
                            </p>

                            <Link
                                href='/contact'
                                className='group mt-5 inline-flex items-center gap-2 font-medium text-background underline decoration-background/30 underline-offset-[6px] transition-colors hover:decoration-background'
                            >
                                Start a conversation
                                <ArrowUpRight className='size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                            </Link>
                        </div>
                    </div>
                </div>

                <div className='mt-16 flex flex-col gap-4 border-t border-background/15 pt-8 text-sm text-background/70 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <a href='#top' className='inline-flex items-center gap-2 transition-colors hover:text-background'>
                        Back to top
                        <ArrowUp className='size-4' />
                    </a>
                </div>
            </div>
        </footer>
    )
}
